<template>
  <v-form id="appointment-form" ref="appointmentForm" class="d-flex flex-column gap-6 flex-grow-1">
    <!--
      In mobile devices the touch event happens instead of the hover event,
      so the hover state is not being reset when the user navigates back to a previous step.
      Ideally we should be able to use @media (hover: hover) {} to solve this, but that would
      require use to re-write the Vuetify theme, and that's not something we can do right now.
    -->
    <v-btn
      :key="currentStep"
      variant="text"
      prepend-icon="mdi-arrow-left"
      class="comet-i18n-exclude align-self-start"
      :class="{ 'back-button--top-right': currentStep === 3 }"
      @click="handleBack">
      {{ currentStep === 3 ? $t('appointmentForm.form') : $t('appointmentForm.back') }}
    </v-btn>

    <div
      class="d-flex flex-column gap-6 flex-grow-1"
      :class="{ 'avoid-step-3': currentStep !== 3 }">
      <!-- Enough space so, in case of overflow, the fixed footer does not overlap the last form field (steps 1–2 only) -->
      <template v-if="currentStep === 1">
        <div class="checkin__form-container comet-i18n-exclude">
          <div class="checkin__form-header">
            <h2 class="typography-heading-md-bold">{{ $t('appointmentForm.purposeTitle') }}</h2>
          </div>

          <div class="d-flex flex-column gap-8">
            <div
              v-for="group in assetVisitTypesGrouped"
              :key="group.label"
              class="d-flex flex-column gap-3">
              <div class="typography-body-md-bold">{{ group.label }}</div>
              <div class="d-flex flex-column gap-2">
                <v-card
                  v-for="option in group.options"
                  :key="option.key"
                  class="option-card"
                  :class="{ 'option-card--selected': selectedOptionKey === option.key }"
                  @click="selectOption(option)"
                  variant="outlined"
                  elevation="1">
                  <v-card-text class="d-flex align-center gap-3">
                    <div class="d-flex align-center justify-center icon-option-container">
                      <template v-for="(icon, index) in option.icons" :key="index">
                        <v-icon>{{ icon }}</v-icon>
                      </template>
                    </div>
                    <div class="flex-grow-1 typography-body-sm-bold">{{ option.label }}</div>
                    <v-icon size="16px">mdi-arrow-right</v-icon>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="currentStep === 2">
        <asset-details-form
          v-if="isYMSEnabled"
          v-model:assetContainerData="assetContainerData"
          :asset-visit-type="assetVisitType"
          :asset-visit-types="getAssetVisitTypesGrouped"
          :should-show-asset-details-form="shouldShowAssetDetailsForm"
          :asset-container-types="assetContainerTypes"
          :max-container-data="MAX_CONTAINER_DATA" />

        <div class="checkin__form-container">
          <v-alert
            v-if="inlineError.msg"
            dense
            outlined
            type="error"
            :icon="inlineError.icon ?? 'mdi-alert-circle-outline'">
            {{ inlineError.msg }}
          </v-alert>

          <v-alert
            v-if="inlineWarning.msg"
            dense
            outlined
            type="warning"
            :icon="inlineWarning.icon ?? 'mdi-alert-circle-outline'">
            {{ inlineWarning.msg }}
          </v-alert>

          <div class="checkin__form-header comet-i18n-exclude">
            <h2 class="typography-heading-md-bold">
              {{ $t('appointmentForm.appointmentInfoTitle') }}
            </h2>
            <h3 class="typography-body-sm text__color-text-tertiary">
              {{ $t('appointmentForm.appointmentInfoHint') }}
            </h3>
          </div>
          <div class="checkin__form-body">
            <div class="custom-field">
              <label
                :class="`custom-field__label ${isRefNumberRequired ? 'required' : ''}`"
                v-if="appointmentIdentifierLabel">
                {{ appointmentIdentifierLabel }}
              </label>
              <v-text-field
                :type="referenceNumberInputMode === 'numeric' ? 'number' : 'text'"
                :inputmode="referenceNumberInputMode === 'numeric' ? 'numeric' : 'text'"
                @keydown="onRefNumKeyDown"
                color="primary"
                variant="outlined"
                data-testid="comet-appointment-identifier"
                v-model="identifier"
                @focus="scrollIntoView"
                @blur="handleRefNumberBlur"
                :rules="referenceNumberRules"
                :error-messages="refNumberError"
                :hint="referenceNumberHint"
                persistent-hint
                validate-on="blur"
                hide-details="auto"></v-text-field>
            </div>
          </div>
        </div>

        <div class="checkin__form-container">
          <div class="checkin__form-header comet-i18n-exclude">
            <h2 class="typography-heading-md-bold">
              {{ $t('appointmentForm.checkInDetailsTitle') }}
            </h2>
            <h3 class="typography-body-sm text__color-text-tertiary">
              {{ $t('appointmentForm.checkInDetailsHint') }}
            </h3>
          </div>

          <div class="checkin__form-body">
            <!-- Company Selector -->
            <company-select
              :warehouse-id="warehouse.id"
              :model-value="companySelectModel"
              @update:model-value="handleCompanySelected" />

            <key-value-list class="comet-i18n-exclude">
              <key-value>
                <template #label>
                  <span class="typography-body-sm-bold">
                    {{ $t('appointmentForm.driverPhoneLabel') }}
                    <span class="text-red">*</span>
                  </span>
                </template>
                <template #value>
                  <phone-number-field-v2
                    v-model="phoneNumber"
                    :default-country="defaultCountry"
                    :input-props="{
                      'data-testid': 'driver-phone-number-field'
                    }"
                    :rules="phoneFieldRules" />

                  <div class="d-flex flex-column gap-2">
                    <v-radio-group inline hide-details v-model="messagingType" color="primary">
                      <v-radio value="SMS" :disabled="!isSMSMessagingEnabled">
                        <template #label>
                          <span class="typography-body-sm">SMS</span>
                        </template>
                      </v-radio>
                      <v-radio value="WhatsApp" :disabled="!isWhatsAppMessagingEnabled">
                        <template #label>
                          <span class="typography-body-sm">WhatsApp</span>
                        </template>
                      </v-radio>
                    </v-radio-group>

                    <v-banner v-if="useWhatsApp" class="whatsapp-banner comet-i18n-exclude">
                      <v-icon size="24">mdi-whatsapp</v-icon>
                      <div class="d-flex flex-column gap-5">
                        <div class="d-flex flex-column gap-1">
                          <div class="typography-body-md-bold">
                            {{ $t('appointmentForm.activateWhatsAppTitle') }}
                          </div>
                          <div class="typography-body-md">
                            {{ $t('appointmentForm.activateWhatsAppBody') }}
                          </div>
                        </div>
                        <div class="typography-body-md-bold">
                          {{ $t('appointmentForm.whatsappDelayBanner') }}
                        </div>
                      </div>
                    </v-banner>
                  </div>
                </template>
              </key-value>
            </key-value-list>

            <!-- Fixed asset visit fields -->
            <custom-fields
              :key="`asset-details-${locale}`"
              :warehouse="warehouse"
              :value="assetDetailsFields" />

            <!-- Custom Forms -->
            <custom-fields
              :key="`custom-forms-${locale}`"
              :warehouse="warehouse"
              v-if="additionalAssetDetailsFieldsExist"
              :value="additionalAssetDetailsFields" />

            <!-- SmartGate fields -->
            <license-plate-select
              v-if="isSmartGateIntegrationEnabled && showLicensePlateSmartGateField"
              :warehouse-id="warehouse.id"
              :model-value="licensePlateSmartGateModel"
              @update:model-value="handleLicensePlateSelected"
              :rules="licensePlateRules" />

            <dot-select
              v-if="isSmartGateIntegrationEnabled && showDotNumberSmartGateField"
              :warehouse-id="warehouse.id"
              :model-value="dotNumberSmartGateModel"
              @update:model-value="handleDotSelected"
              :rules="dotNumberRules" />

            <!-- These notes will be appended to the appointment.notes -->
            <div class="custom-field comet-i18n-exclude">
              <label class="custom-field__label">{{ $t('appointmentForm.notesLabel') }}</label>
              <v-textarea
                color="primary"
                variant="outlined"
                data-testid="comet-appointment-notes"
                v-model="notes"
                :placeholder="$t('appointmentForm.notesPlaceholder')"
                maxlength="4096"
                counter="4096"
                hide-details="auto"
                clearable />
            </div>
          </div>
        </div>
      </template>

      <template v-if="currentStep === 3">
        <id-verification
          :warehouse="warehouse"
          @complete="handleIdVerificationComplete"
          @error="handleIdVerificationError"
          @skip="handleIdVerificationSkip" />
      </template>
    </div>

    <div class="checkin__button-container comet-i18n-exclude" v-if="currentStep === 2">
      <v-btn
        block
        :prepend-icon="isIdVerificationEnabled ? undefined : 'mdi-check'"
        :append-icon="isIdVerificationEnabled ? 'mdi-arrow-right' : undefined"
        :color="!isStepValid ? 'none' : 'primary'"
        :disabled="!isStepValid || validatingReference || submitting || !!inlineError.msg"
        :loading="validatingReference"
        @click="handleGoToIdVerification">
        {{
          isIdVerificationEnabled
            ? $t('appointmentForm.verifyAndCheckIn')
            : $t('appointmentForm.completeCheckIn')
        }}
      </v-btn>
    </div>
  </v-form>
</template>

<script lang="ts">
import {
  AssetVisitType,
  AssetContainerType,
  WarehouseFeatureEnum,
  isFeatureEnabledForWarehouse,
  ReferenceNumberInputModes,
  EntitySettings,
  ObservationMatchAttribute,
  AssetVisitDirection
} from '@nova/core';
import { defineComponent, ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useFlag } from '@loadsmart/vue-feature-flags';
import type { VForm } from 'vuetify/components';
import { get, omit, trim } from 'lodash';
import axios from 'axios';
import type { PropType } from 'vue';
import { isString, isUUID } from 'class-validator';
import { useI18n } from 'vue-i18n';

import {
  clearLocalStorageKey,
  updateObjectInLocalStorage
} from '@/components/mixins/localStorageMixin';
import { KeyValueList, KeyValue } from '@satellite/components/elements/key_value';
import { useApiErrors, useCheckinForms } from '@/components/composables';
import useCometBffApi from '@/components/composables/useCometBffApi';
import { getSenderPhoneNumber } from '@/config';
import AssetDetailsForm from './AssetDetailsForm.vue';
import IdVerification from './IdVerification.vue';
import PhoneNumberFieldV2 from '@satellite/components/elements/forms/PhoneNumberFieldV2.vue';

import type { IWarehouse, IAppointment } from '@nova/core';

const MAX_CONTAINER_DATA = 3;

type MessagingType = 'SMS' | 'WhatsApp';

type SmartGateFields = {
  licensePlate: string | null;
  dotNumber: string | null;
};

export default defineComponent({
  name: 'AppointmentForm',
  components: {
    AssetDetailsForm,
    IdVerification,
    PhoneNumberFieldV2,
    KeyValueList,
    KeyValue
  },
  props: {
    latitude: {
      type: String,
      required: true
    },
    longitude: {
      type: String,
      required: true
    },
    warehouse: {
      type: Object as PropType<IWarehouse>,
      required: true
    }
  },
  emits: ['back', 'step-change'],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const router = useRouter();
    const rejectedAssetVisitFlag = useFlag('enable-rejected-asset-visit-status');
    const { makeErrorScreenParams } = useApiErrors();
    const {
      assetDetailsFields,
      currentStep,
      additionalAssetDetailsFields,
      additionalAssetDetailsFieldsExist,
      additionalAssetDetailsTriggers,
      areRequiredAssetDetailsFilled,
      setRequiredAssetVisitFields,
      setCurrentStep,
      extractFieldsFromTriggers,
      getAssetDetailsData,
      prepareCustomFormsData
    } = useCheckinForms(props.warehouse);
    const { resolveAppointment, resolveCheckinPlanned } = useCometBffApi();

    setCurrentStep(isFeatureEnabledForWarehouse(props.warehouse, WarehouseFeatureEnum.YMS) ? 1 : 2);

    watch(
      currentStep,
      step => {
        emit('step-change', step);
      },
      { immediate: true }
    );

    const isStepValid = ref(false);
    const isIdVerificationComplete = ref(false);
    const idVerificationResult = ref<{
      inquiryId?: string;
      status?: string;
      success?: boolean;
    } | null>(null);
    const assetVisitType = ref(AssetVisitType.Live);
    const assetContainerData = ref([
      { containerCode: '', containerType: AssetContainerType.Trailer }
    ]);

    const assetVisitTypesGrouped = computed(() => [
      {
        group: AssetVisitDirection.Inbound,
        label: t('appointmentForm.visitGroupInbound'),
        options: [
          {
            key: 'unload',
            value: AssetVisitType.LiveUnload,
            direction: AssetVisitDirection.Inbound,
            label: t('appointmentForm.visitUnloadTruck'),
            icons: ['mdi-arrow-left-thin', 'mdi-truck-outline']
          },
          {
            key: 'drop',
            value: AssetVisitType.Drop,
            direction: AssetVisitDirection.Inbound,
            label: t('appointmentForm.visitDropTrailer'),
            icons: ['mdi-hook-off']
          }
        ]
      },
      {
        group: AssetVisitDirection.Outbound,
        label: t('appointmentForm.visitGroupOutbound'),
        options: [
          {
            key: 'load',
            value: AssetVisitType.LiveLoad,
            direction: AssetVisitDirection.Outbound,
            label: t('appointmentForm.visitLoadTruck'),
            icons: ['mdi-arrow-right-thin', 'mdi-truck-outline']
          },
          {
            key: 'pickup',
            value: AssetVisitType.PickUp,
            direction: AssetVisitDirection.Outbound,
            label: t('appointmentForm.visitHookTrailer'),
            icons: ['mdi-hook']
          }
        ]
      },
      {
        group: AssetVisitDirection.InboundAndOutbound,
        label: t('appointmentForm.visitGroupBoth'),
        options: [
          {
            key: 'unload-load',
            value: AssetVisitType.Live,
            direction: AssetVisitDirection.InboundAndOutbound,
            label: t('appointmentForm.visitUnloadLoad'),
            icons: ['mdi-swap-horizontal', 'mdi-truck-outline']
          },
          {
            key: 'drop-hook',
            value: AssetVisitType.DropHook,
            direction: AssetVisitDirection.InboundAndOutbound,
            label: t('appointmentForm.visitDropHook'),
            icons: ['mdi-hook-off', 'mdi-hook']
          }
        ]
      }
    ]);

    const selectedOptionKey = ref('unload');

    const handleNext = () => {
      setCurrentStep(currentStep.value + 1);
    };

    function selectOption(option: {
      key: string;
      value: AssetVisitType;
      direction: AssetVisitDirection;
      icons: Array<string>;
    }) {
      selectedOptionKey.value = option.key;
      assetVisitType.value = option.value;
      handleNext();
    }

    const defaultCountry = ref('US');
    const messagingType = ref<MessagingType>('SMS');
    const isSMSMessagingEnabled = ref(false);
    const isWhatsAppMessagingEnabled = ref(false);

    const updateMessagingSettings = (warehouse: IWarehouse) => {
      const country = get(warehouse, 'country') || 'USA';
      defaultCountry.value = country;
      messagingType.value = ['USA', 'CAN'].includes(country) ? 'SMS' : 'WhatsApp';
      isSMSMessagingEnabled.value = messagingType.value === 'SMS';
      isWhatsAppMessagingEnabled.value = messagingType.value === 'WhatsApp';
    };

    // Initialize with current warehouse
    updateMessagingSettings(props.warehouse);

    const assetContainerTypes = computed(() => [
      { title: t('appointmentForm.containerTrailer'), value: AssetContainerType.Trailer },
      { title: t('appointmentForm.containerContainer'), value: AssetContainerType.Container },
      { title: t('appointmentForm.containerOversized'), value: AssetContainerType.Oversized },
      { title: t('appointmentForm.containerOther'), value: AssetContainerType.Other }
    ]);

    const phoneFieldRules = computed(() => [
      (
        value: string,
        phone: { valid?: boolean } | null | undefined,
        messageOptions?: { example?: string }
      ) => {
        if (typeof value !== 'string' || value.trim().length === 0) {
          return t('common.validation.fieldRequired');
        }

        const { example } = messageOptions ?? {};
        if (phone?.valid) {
          return true;
        }
        return example
          ? t('common.validation.phoneInvalidWithExample', { example })
          : t('common.validation.phoneInvalid');
      }
    ]);

    const requiredValidator = (value: string, label: string) =>
      !value || value?.length === 0
        ? t('common.validation.fieldRequiredWithLabel', { label })
        : true;

    return {
      additionalAssetDetailsFields,
      additionalAssetDetailsFieldsExist,
      additionalAssetDetailsTriggers,
      areRequiredAssetDetailsFilled,
      assetContainerData,
      AssetContainerType,
      AssetVisitType,
      assetContainerTypes,
      assetDetailsFields,
      assetVisitType,
      assetVisitTypesGrouped,
      currentStep,
      defaultCountry,
      messagingType,
      isSMSMessagingEnabled,
      isWhatsAppMessagingEnabled,
      isStepValid,
      isIdVerificationComplete,
      idVerificationResult,
      MAX_CONTAINER_DATA,
      phoneFieldRules,
      requiredValidator,
      selectedOptionKey,
      extractFieldsFromTriggers,
      getAssetDetailsData,
      locale,
      makeErrorScreenParams,
      prepareCustomFormsData,
      selectOption,
      setCurrentStep,
      setRequiredAssetVisitFields,
      updateMessagingSettings,
      router,
      rejectedAssetVisitFlag,
      resolveAppointment,
      resolveCheckinPlanned
    };
  },
  data() {
    return {
      submitting: false,
      validatingReference: false,
      refNumberError: '',
      notes: '',
      phoneNumber: '',
      identifier: '',
      companyId: null as string | null,
      companyName: null as string | null,
      licensePlate: null as string | null,
      dotNumber: null as string | null,
      appointment: {} as IAppointment,
      inlineError: {
        msg: '',
        icon: ''
      },
      inlineWarning: {
        msg: '',
        icon: ''
      }
    };
  },
  computed: {
    appointmentIdentifierLabel() {
      const referenceDisplayName =
        this.warehouse.settings?.referenceNumberDisplayName ??
        this.$t('appointmentForm.defaultReferenceName');
      return this.$t('appointmentForm.identifierSuffix', {
        reference: referenceDisplayName
      });
    },
    referenceNumberInputMode(): ReferenceNumberInputModes {
      const setting =
        this.warehouse.settings?.referenceNumberInputMode ||
        EntitySettings.Org.referenceNumberInputMode.defaultValue;
      return setting as ReferenceNumberInputModes;
    },
    referenceNumberRules() {
      return [
        (value: string) => {
          if (this.isRefNumberRequired) {
            return !!value?.trim() || this.$t('common.validation.fieldRequired');
          }
        }
      ];
    },
    useWhatsApp() {
      return this.messagingType === 'WhatsApp';
    },
    isYMSEnabled() {
      return isFeatureEnabledForWarehouse(this.warehouse, WarehouseFeatureEnum.YMS);
    },
    isSmartGateIntegrationEnabled() {
      return isFeatureEnabledForWarehouse(this.warehouse, WarehouseFeatureEnum.SmartGate);
    },
    observationMatchAttribute() {
      return this.warehouse.settings?.observationEventMatchAttribute;
    },
    showAnySmartGateFields() {
      return this.observationMatchAttribute === ObservationMatchAttribute.ANY;
    },
    showLicensePlateSmartGateField() {
      return (
        this.observationMatchAttribute === ObservationMatchAttribute.LICENSE_PLATE ||
        this.showAnySmartGateFields
      );
    },
    showDotNumberSmartGateField() {
      return (
        this.observationMatchAttribute === ObservationMatchAttribute.DOT_NUMBER ||
        this.showAnySmartGateFields
      );
    },
    getAssetVisitTypesGrouped() {
      return this.assetVisitTypesGrouped
        .flatMap(group => group.options)
        .map(option => ({
          value: option.value,
          label: option.label,
          direction: option.direction,
          icons: option.icons
        }));
    },
    licensePlateRules() {
      if (this.observationMatchAttribute === ObservationMatchAttribute.ANY) {
        return [
          (value: { id: string | null; name: string } | null) => {
            const licensePlateValue = value?.name?.trim() || '';
            const dotNumberValue = this.dotNumber?.trim() || '';
            if (!licensePlateValue && !dotNumberValue) {
              return this.$t('appointmentForm.licenseOrDotRequired');
            }
            return true;
          }
        ];
      } else if (this.observationMatchAttribute === ObservationMatchAttribute.LICENSE_PLATE) {
        return [
          (value: { id: string | null; name: string } | null) => {
            if (!value || typeof value.name !== 'string' || value.name.trim().length === 0) {
              return this.$t('common.validation.fieldRequired');
            }
            return true;
          }
        ];
      }
      // No validation needed for other cases
      return [];
    },
    dotNumberRules() {
      if (this.observationMatchAttribute === ObservationMatchAttribute.ANY) {
        // When ANY, at least one field (license plate OR dot number) must be filled
        return [
          (value: { id: string | null; name: string } | null) => {
            const dotNumberValue = value?.name?.trim() || '';
            const licensePlateValue = this.licensePlate?.trim() || '';
            if (!dotNumberValue && !licensePlateValue) {
              return this.$t('appointmentForm.licenseOrDotRequired');
            }
            return true;
          }
        ];
      } else if (this.observationMatchAttribute === ObservationMatchAttribute.DOT_NUMBER) {
        // When DOT_NUMBER, this field is required
        return [
          (value: { id: string | null; name: string } | null) => {
            if (!value || typeof value.name !== 'string' || value.name.trim().length === 0) {
              return this.$t('common.validation.fieldRequired');
            }
            return true;
          }
        ];
      }
      // No validation needed for other cases
      return [];
    },
    shouldShowAssetDetailsForm() {
      // Hide asset details form when the visit type is "Hook a trailer or container" (PickUp)
      return this.assetVisitType !== AssetVisitType.PickUp;
    },
    isIdVerificationEnabled() {
      const isEnabled = this.warehouse.settings?.enableCheckinIdValidation === true;
      const templateId = this.warehouse.settings?.checkinIdValidationTemplateId;
      return isEnabled && Boolean(templateId);
    },
    isIdValidationRequired() {
      return this.warehouse.settings?.checkinIdValidationRequired === true;
    },
    isRefNumberRequired() {
      return this.warehouse.settings?.gateManagementDriverReferenceRequired === true;
    },
    referenceNumberHint() {
      return this.isRefNumberRequired && !this.identifier?.trim()
        ? this.$t('appointmentForm.referenceCaseSensitiveHint')
        : '';
    },
    isRefNumberValidated() {
      return this.warehouse.settings?.gateManagementDriverReferenceValidated === true;
    },
    /** Hydrate carrier/SmartGate fields after step 2 remounts (navigating back from ID verification). */
    companySelectModel(): { id: string | null; name: string } | null {
      if (!this.companyId && !this.companyName) {
        return null;
      }
      return {
        id: this.companyId,
        name: this.companyName ?? ''
      };
    },
    licensePlateSmartGateModel(): { name: string } | null {
      if (!this.licensePlate) {
        return null;
      }
      return { name: this.licensePlate };
    },
    dotNumberSmartGateModel(): { name: string } | null {
      if (!this.dotNumber) {
        return null;
      }
      return { name: this.dotNumber };
    }
  },
  methods: {
    async handleRefNumberBlur(): Promise<void> {
      const refAtBlur = this.identifier?.trim();
      if (!this.isRefNumberRequired || !refAtBlur) return;

      if (this.isRefNumberValidated) {
        this.refNumberError = '';
        this.validatingReference = true;
        try {
          const { found } = await this.checkApptExists();
          if (this.identifier?.trim() === refAtBlur && !found) {
            this.refNumberError = this.$t('appointmentForm.referenceNotFound');
          }
        } catch {
          // non-404 errors are surfaced on submit with full context
        } finally {
          if (this.identifier?.trim() === refAtBlur) {
            this.validatingReference = false;
          }
        }
      }
    },
    onRefNumKeyDown(event: KeyboardEvent) {
      // Firefox, unlike Chrome, does not prevent letters from being typed in number input
      if (this.referenceNumberInputMode === 'numeric') {
        const key = event.key;
        // eslint-disable-next-line no-useless-escape
        const isLetterOrSymbolKey = /^[a-zA-Z+*&^@#;()%<>!~\-\[\]{}=\s]$/.test(key);
        if (isLetterOrSymbolKey) {
          event.preventDefault();
        }
      }
    },
    scrollIntoView($e: FocusEvent) {
      const targetElement = $e.target as Element;
      targetElement?.scrollIntoView({ behavior: 'smooth' });
    },
    setCustomFields() {
      this.additionalAssetDetailsFields = this.extractFieldsFromTriggers(
        this.additionalAssetDetailsTriggers
      );
    },
    setFixedFields() {
      this.companyId = null;
      this.companyName = null;
      this.phoneNumber = '';
    },
    handleBack() {
      // Goes back to the initial page
      if (!this.isYMSEnabled) {
        this.$emit('back');
        return;
      }

      if (this.currentStep <= 1) {
        this.$emit('back');
      } else {
        this.setCurrentStep(this.currentStep - 1);
      }
    },
    handleCompanySelected(value: { id?: string; name: string }) {
      // reset on each change to avoid stale values
      this.companyId = null;
      this.companyName = null;

      if (!value || !value?.name) {
        return;
      }

      if (isString(value)) {
        const name = value.trim();
        this.companyName = name.length ? name : null;
        return;
      }

      const { id, name } = value;

      if (isString(id) && isUUID(id)) {
        this.companyId = id;
      }
      this.companyName = (name ?? '').trim() || null;
    },
    handleLicensePlateSelected(value: { name: string }) {
      // reset on each change to avoid stale values
      this.licensePlate = (value?.name ?? '').trim() || null;
    },
    handleDotSelected(value: { name: string }) {
      // reset on each change to avoid stale values
      this.dotNumber = (value?.name ?? '').trim() || null;
    },
    async handleGoToIdVerification() {
      if (this.$refs.appointmentForm) {
        const { valid } = await (this.$refs.appointmentForm as VForm).validate();
        if (!valid) return;
      }

      const shouldValidateRef =
        (this.isRefNumberValidated || this.isIdVerificationEnabled) &&
        Boolean(this.identifier?.trim());

      if (!shouldValidateRef) {
        this.advanceFromStep2();
        return;
      }

      this.inlineError.msg = '';
      this.inlineWarning.msg = '';
      this.validatingReference = true;
      this.checkApptExists()
        .then(({ found }) => {
          if (this.isRefNumberValidated && !found) {
            this.refNumberError = this.$t('appointmentForm.referenceNotFound');
            return;
          }
          this.advanceFromStep2();
        })
        .catch(err => {
          if (axios.isAxiosError(err)) {
            const errorParams = this.makeErrorScreenParams(err);
            if (errorParams.showErrorInline !== '1') {
              this.$router.push({
                name: 'appointment-error',
                query: errorParams
              });
            } else {
              this.inlineError.msg = errorParams.title;
              this.inlineError.icon = errorParams.icon;
            }
          } else {
            this.inlineError.msg = this.$t('appointmentForm.appointmentNotFound');
            this.inlineError.icon = 'mdi-alert-circle-outline';
          }
        })
        .finally(() => {
          this.validatingReference = false;
        });
    },
    advanceFromStep2() {
      if (this.isIdVerificationEnabled) {
        this.currentStep = 3;
      } else {
        this.submit();
      }
    },
    handleIdVerificationComplete(result: {
      inquiryId?: string;
      status?: string;
      success?: boolean;
    }) {
      this.idVerificationResult = result;
      this.isIdVerificationComplete = true;
      this.submit();
    },
    handleIdVerificationError(error: { inquiryId?: string; status?: string; message?: string }) {
      console.error('ID verification error:', error);
      this.idVerificationResult = { ...error, success: false };

      if (!this.isIdValidationRequired) {
        this.isIdVerificationComplete = true;
        this.submit();
      } else if (this.rejectedAssetVisitFlag?.enabled) {
        const assetDetails = this.getAssetDetailsData();

        const smartGateFields: Partial<SmartGateFields> = {};
        if (this.isSmartGateIntegrationEnabled) {
          smartGateFields.licensePlate = this.licensePlate;
          smartGateFields.dotNumber = this.dotNumber;
        }

        updateObjectInLocalStorage('comet:reject-payload', {
          assetDetails: {
            ...assetDetails,
            assetVisitType: this.assetVisitType,
            companyId: this.companyId,
            companyHint: this.companyName,
            driverAppointmentIdentifier: this.identifier,
            driverLanguageTag: this.locale,
            phone: this.phoneNumber,
            useWhatsApp: this.useWhatsApp
          },
          customFormsData: this.prepareCustomFormsData(),
          warehouseId: this.warehouse.id,
          assetContainerDetails: this.assetContainerData,
          inquiryId: error.inquiryId,
          ...smartGateFields
        });
        this.router.push({ name: 'rejected-check-in' });
      }
    },
    handleIdVerificationSkip(data: { reason: string; event?: unknown }) {
      console.warn('ID verification skipped:', data.reason);
      this.idVerificationResult = { success: false };
      this.isIdVerificationComplete = true;
      this.submit();
    },
    getMessagingTypeSpecificFailedMessage() {
      const senderPhoneNumber = getSenderPhoneNumber();
      const displayPhone =
        this.phoneNumber?.trim() || this.$t('appointmentForm.messaging.fallbackPhone');
      const waUrl = `https://wa.me/${senderPhoneNumber}`;
      const smsUrl = `sms:${senderPhoneNumber}`;

      if (this.useWhatsApp) {
        return {
          errorTitle: this.$t('appointmentForm.messaging.whatsappFailedTitle'),
          errorMessage: this.$t('appointmentForm.messaging.whatsappFailedBody', {
            displayPhone,
            waUrl,
            senderPhone: senderPhoneNumber
          })
        };
      }

      return {
        errorTitle: this.$t('appointmentForm.messaging.smsFailedTitle'),
        errorMessage: this.$t('appointmentForm.messaging.smsFailedBody', {
          displayPhone,
          smsUrl,
          senderPhone: senderPhoneNumber
        })
      };
    },
    storeMiscData() {
      /**
       * We use these values in the Complete page to compose the correct instructions.
       */
      updateObjectInLocalStorage('comet:misc', {
        isPlanned: Boolean(this.appointment?.id),
        useWhatsApp: this.useWhatsApp
      });
    },
    async submit() {
      this.inlineError.msg = '';
      this.inlineWarning.msg = '';

      this.submitting = true;

      const warehouseId = this.warehouse?.id || 'N/A';
      const warehouseName = this.warehouse?.name || 'N/A';
      const orgId = this.warehouse?.orgId || 'N/A';
      const orgName = this.warehouse?.org?.name || 'N/A';

      try {
        if (this.identifier) {
          await this.checkApptExists();
        }

        const assetDetails = this.getAssetDetailsData();
        const appointmentId = this.appointment?.id;
        const notes = this.notes?.replace(/\n/g, '<br />') || '';

        this.storeMiscData();

        const smartGateFields: Partial<SmartGateFields> = {};
        if (this.isSmartGateIntegrationEnabled) {
          smartGateFields.licensePlate = this.licensePlate;
          smartGateFields.dotNumber = this.dotNumber;
        }

        let inquiryId: string | undefined;
        if (this.isIdVerificationEnabled && this.idVerificationResult?.inquiryId) {
          inquiryId = this.idVerificationResult.inquiryId;
        }

        const payload = {
          latitude: this.latitude,
          longitude: this.longitude,
          appointmentId: appointmentId || null,
          assetDetails: {
            ...assetDetails,
            assetVisitType: this.assetVisitType,
            companyId: this.companyId,
            companyHint: this.companyName,
            driverNotes: notes,
            driverAppointmentIdentifier: this.identifier,
            driverLanguageTag: this.locale,
            phone: this.phoneNumber,
            useWhatsApp: this.useWhatsApp,
            ...smartGateFields
          },
          customFormsData: this.prepareCustomFormsData(),
          warehouseId: this.warehouse.id,
          assetContainerDetails: this.assetContainerData,
          notes,
          inquiryId
        };

        let data = null;
        if (appointmentId) {
          data = await this.submitPlanned(payload);
        } else {
          data = await this.submitUnplanned(payload);
        }

        if (data) {
          const assetVisit = get(data, 'appointment.assetVisit') ?? get(data, 'assetVisit');
          const appointment = get(data, 'appointment');
          const customFormsData = get(data, 'customFormsData') ?? [];
          const firstCustomFormTrigger = get(data, 'customFormsData.0.trigger');

          this.mixpanel.track('Completed: Self Check-in', {
            'Appointment ID': appointmentId || 'N/A',
            'Warehouse ID': warehouseId,
            'Warehouse Name': warehouseName,
            'Org ID': orgId,
            'Org Name': orgName,
            'Appointment Validation #':
              appointment?.refNumber || appointment?.confirmationNumber || 'N/A',
            'Asset Visit Type': assetVisit?.visitType || 'N/A',
            'Asset Visit ID': assetVisit?.id || 'N/A',
            'Entry Point': 'Self Check-in',
            'Check-in Type': appointmentId ? 'Planned Arrival' : 'Unplanned Arrival'
          });

          if (Array.isArray(customFormsData) && customFormsData.length > 0) {
            this.mixpanel.track('Submitted: Custom Form', {
              'Custom Form Application': firstCustomFormTrigger?.app,
              'Custom Form Type': firstCustomFormTrigger?.feature,
              'Custom Form Trigger': firstCustomFormTrigger?.category,
              'Appointment ID': appointmentId || 'N/A',
              'Warehouse ID': warehouseId,
              'Warehouse Name': warehouseName,
              'Org ID': orgId,
              'Org Name': orgName,
              'Entry Point': 'Self Check-in'
            });
          }
        }
        this.router.push({
          name: 'completed',
          query: data.initialMessageFailed ? this.getMessagingTypeSpecificFailedMessage() : {}
        });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const errorParams = this.makeErrorScreenParams(err);

          this.mixpanel.track('Error: Self Check-in', {
            'Appointment Validation #':
              this.appointment?.refNumber ||
              this.appointment?.confirmationNumber ||
              this.identifier ||
              'N/A',
            'Warehouse ID': warehouseId,
            'Warehouse Name': warehouseName,
            'Org ID': orgId,
            'Org Name': orgName,
            Latitude: this.latitude || 'N/A',
            Longitude: this.longitude || 'N/A',
            'Error Type': errorParams.title,
            'Check-in Type': this.appointment?.id ? 'Planned Arrival' : 'Unplanned Arrival'
          });

          if (errorParams.showErrorInline !== '1') {
            this.router.push({
              name: 'appointment-error',
              query: errorParams
            });
          } else {
            this.inlineError.msg = errorParams.title;
            this.inlineError.icon = errorParams.icon;
          }
        }
      } finally {
        this.submitting = false;
      }
    },
    async checkApptExists(): Promise<{ found: boolean }> {
      try {
        const { url, headers } = await this.resolveAppointment(this.warehouse.id, this.identifier, {
          latitude: Number(this.latitude),
          longitude: Number(this.longitude)
        });
        const res = await this.axiosClient.get(url, { headers });
        this.appointment = { ...res.data.data };
        updateObjectInLocalStorage('comet:appointment', this.appointment);
        return { found: true };
      } catch (error) {
        this.appointment = {} as IAppointment;
        clearLocalStorageKey('comet:appointment');

        const status = get(error, 'response.status');

        /**
         * If the appointment is not found (error 404), we want to continue with the
         * submit process when called from submit(), so we handle it as unplanned.
         * When called from step 2, the caller will redirect to the appointment not found screen.
         * For any other status (e.g. 400), we throw so the user sees the error.
         */

        if (status === 404) {
          return { found: false };
        }
        throw error;
      }
    },
    async submitUnplanned(payload: Record<string, unknown>) {
      const { data } = await this.axiosClient.put(
        `/checkin/public/${this.warehouse.id}/unplanned`,
        payload
      );

      return data;
    },
    async submitPlanned(payload: Record<string, unknown>) {
      const { url, headers } = await this.resolveCheckinPlanned(this.warehouse.id);

      // The shared payload builder includes fields only the unplanned flow accepts
      // (warehouseId comes from the URL; companyId/companyHint/driverNotes only exist
      // on the unplanned DTO). The legacy route silently stripped them server-side;
      // the BFF route rejects unknown fields, so strip them before sending.
      const plannedPayload = {
        ...omit(payload, ['warehouseId']),
        assetDetails: omit(payload.assetDetails as Record<string, unknown>, [
          'companyId',
          'companyHint',
          'driverNotes'
        ])
      };
      const res = await this.axiosClient.put(url, plannedPayload, { headers });

      // The BFF response is wrapped in a { data } envelope; the legacy route is flat.
      return res.data.data ?? res.data;
    },
    async validateStep() {
      if (this.currentStep === 1) {
        this.isStepValid = true;
      } else if (this.currentStep === 2) {
        // Validate asset container data if YMS is enabled and asset details form is shown
        let isAssetContainerDataValid = true;
        if (this.isYMSEnabled && this.shouldShowAssetDetailsForm) {
          isAssetContainerDataValid = this.assetContainerData.every(
            ({ containerCode, containerType }) =>
              trim(containerCode).length > 0 && trim(containerType).length > 0
          );
        }

        this.isStepValid = isAssetContainerDataValid && this.areRequiredAssetDetailsFilled;

        const isPhoneProvided = Boolean(this.phoneNumber?.trim());
        this.isStepValid = this.isStepValid && isPhoneProvided;

        const isCompanySelected = Boolean(this.companyId || this.companyName);
        this.isStepValid = this.isStepValid && isCompanySelected;

        if (this.isRefNumberRequired) {
          this.isStepValid = this.isStepValid && Boolean(this.identifier?.trim());
        }

        // Validate SmartGate fields
        if (this.isSmartGateIntegrationEnabled) {
          const areSmartGateFieldsFilled = this.licensePlate || this.dotNumber;

          switch (this.observationMatchAttribute) {
            case ObservationMatchAttribute.ANY:
              this.isStepValid = this.isStepValid && Boolean(areSmartGateFieldsFilled);
              break;
            case ObservationMatchAttribute.LICENSE_PLATE:
              this.isStepValid = this.isStepValid && Boolean(this.licensePlate);
              break;
            case ObservationMatchAttribute.DOT_NUMBER:
              this.isStepValid = this.isStepValid && Boolean(this.dotNumber);
              break;
            default:
              this.inlineWarning.msg = '';
              this.inlineWarning.icon = '';
              break;
          }
        }
      } else if (this.currentStep === 3) {
        // Step 3 validation is handled by isIdVerificationComplete
        this.isStepValid = this.isIdVerificationComplete;
      } else {
        this.isStepValid = false;
      }
    }
  },
  mounted() {
    this.setCustomFields();
    this.setRequiredAssetVisitFields();
    this.setFixedFields();
  },
  watch: {
    warehouse() {
      this.setCustomFields();
      this.setRequiredAssetVisitFields();
      this.setFixedFields();
      this.updateMessagingSettings(this.warehouse);
    },
    currentStep: {
      immediate: true,
      handler() {
        const focusFirstStep2InputAndScroll = () => {
          const appointmentForm = document.getElementById('appointment-form');
          if (!appointmentForm) {
            return;
          }

          /**
           * Nice-to-have UX: focus the first input when landing on step 2.
           */
          if (this.currentStep === 2) {
            if (this.isYMSEnabled && this.shouldShowAssetDetailsForm) {
              const firstInputWrapper = appointmentForm.querySelector(
                '[data-testid="asset-visit-form-container-code-0"]'
              );
              const firstInput = firstInputWrapper?.querySelector('input');

              if (firstInput) {
                firstInput.focus();
              }
            } else {
              const firstInput = appointmentForm.querySelector('input');

              if (firstInput) {
                firstInput.focus();
              }
            }
          }

          appointmentForm.scrollIntoView({ behavior: 'smooth' });
        };

        // Step 2 content is v-if-mounted; validate after children exist so VForm rules run correctly.
        if (this.currentStep === 2) {
          this.$nextTick(() => {
            void this.validateStep();
            focusFirstStep2InputAndScroll();
          });
        } else {
          void this.validateStep();
          this.$nextTick(focusFirstStep2InputAndScroll);
        }
      }
    },
    assetContainerData: {
      deep: true,
      handler() {
        this.validateStep();
      }
    },
    notes: {
      handler() {
        if (this.currentStep === 2) {
          this.validateStep();
        }
      }
    },
    identifier: {
      handler() {
        if (this.currentStep === 2) {
          this.inlineError.msg = '';
          this.refNumberError = '';
          this.validateStep();
        }
      }
    },
    assetDetailsFields: {
      deep: true,
      handler() {
        if (this.currentStep === 2) {
          this.validateStep();
        }
      }
    },
    additionalAssetDetailsFields: {
      deep: true,
      handler() {
        if (this.currentStep === 2) {
          this.validateStep();
        }
      }
    },
    areRequiredAssetDetailsFilled() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    areRequiredCheckinFieldsFilled() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    companyId() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    companyName() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    phoneNumber() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    licensePlate() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    dotNumber() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    isIdVerificationComplete() {
      if (this.currentStep === 3) {
        this.validateStep();
      }
    }
  }
});
</script>

<style lang="scss">
.option-card {
  border-radius: 8px;
  border: 1px solid $color-neutral-40 !important;
  cursor: pointer;
}
.option-card--selected {
  border-color: #005a87;
  border-width: 2px;
}

.checkin__form-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.checkin__form-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.checkin__form-body {
  display: flex;
  flex-direction: column;
  gap: 16px;

  border-radius: 4px;
  border: 1px solid #ebebeb;
  background-color: #fff;

  padding: 16px;
}

.checkin__button-container {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  background: white;
  border-top: 1px solid #ebebeb;
  padding: $spacing-4;
}

.whatsapp-banner {
  background-color: $color-warning-20 !important;
  color: $color-text-primary !important;
  padding: $spacing-6 !important;

  .v-banner__content {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: $spacing-4;
  }
}

.icon-option-container {
  width: 42px;
  min-width: 42px;
}

.back-button--top-right {
  position: absolute;
  top: 14px;
  right: 8px;
  z-index: 1000;
  background-color: $color-neutral-10;
}

.avoid-step-3 {
  padding: 0px $spacing-4 calc(72px + 16px);
}
</style>
